/**
 * Pure presentation functions for the workflow editor (design §14.10, §14.11).
 *
 * No Angular, no DOM, no state: every function maps server data to the words, colors and labels
 * the editor shows, so the copy is defined (and tested) in one place.
 */
import type {
    CanvasElement,
    FlowIssue,
    Lifecycle,
    PaletteKind,
    ParticipantView,
    PublicationState,
} from '../../../../models/workflow.model';

/** Number of role accent colors (`--color-role-1` … `--color-role-8` in `styles.css`). */
export const ROLE_COLOR_COUNT = 8;

/** Avatar initials: first letters of up to two words, upper-cased; `?` when blank. */
export function initials(name: string): string {
    const letters = name
        .trim()
        .split(/\s+/)
        .filter((word) => word.length > 0)
        .slice(0, 2)
        .map((word) => Array.from(word)[0]);
    return letters.length === 0 ? '?' : letters.join('').toUpperCase();
}

/**
 * The role accent color index (1..8) for a `RoleId`, stable across sessions: FNV-1a 32-bit over
 * the string's UTF-16 code units, mod 8, 1-based. `0` (the muted accent) for `''` (no role).
 */
export function roleColorIndex(roleId: string): number {
    if (roleId === '') {
        return 0;
    }
    let hash = 0x811c9dc5;
    for (let i = 0; i < roleId.length; i++) {
        hash ^= roleId.charCodeAt(i);
        hash = Math.imul(hash, 0x01000193) >>> 0;
    }
    return (hash % ROLE_COLOR_COUNT) + 1;
}

/** Who edited, as shown in the UI (22.3): "you" for the signed-in user, else "another member". */
export function editorLabel(userId: string, me: string): string {
    return userId !== '' && userId === me ? 'you' : 'another member';
}

/** The publication-state badge copy (17.2). */
export function publicationLabel(state: PublicationState): string {
    switch (state) {
        case 'draft':
            return 'Draft';
        case 'published':
            return 'Published';
        case 'published-with-changes':
            return 'Published with unpublished changes';
    }
}

/** The lifecycle tag copy. */
export function lifecycleLabel(lifecycle: Lifecycle): string {
    return lifecycle === 'archived' ? 'Archived' : 'Active';
}

const KIND_LABELS: Readonly<Record<PaletteKind, string>> = {
    manual: 'Manual trigger',
    schedule: 'Schedule trigger',
    event: 'Event trigger',
    step: 'Step',
    agentDecision: 'Agent decision',
    ruleDecision: 'Rule decision',
    end: 'End',
};

/** Human label of a palette / canvas element kind (18.3). */
export function kindLabel(kind: PaletteKind): string {
    return KIND_LABELS[kind];
}

/** Whether an element of this kind is performed by a participant (Step, Agent decision). */
export function hasParticipant(kind: PaletteKind): boolean {
    return kind === 'step' || kind === 'agentDecision';
}

function plural(count: number, noun: string): string {
    return `${count} ${noun}${count === 1 ? '' : 's'}`;
}

/**
 * The node card's accessible name (24.4):
 * "<Kind> <name>, <participant or Unassigned>, <n> errors, <m> warnings".
 * The participant part is present only for kinds a participant performs (Step, Agent decision);
 * triggers, rule decisions and ends have no participant to announce.
 */
export function nodeAriaLabel(
    element: CanvasElement,
    participant: ParticipantView | undefined,
    counts: { errors: number; warnings: number },
): string {
    const parts = [`${kindLabel(element.kind)} ${element.name}`];
    if (hasParticipant(element.kind)) {
        parts.push(participant?.name || 'Unassigned');
    }
    parts.push(plural(counts.errors, 'error'), plural(counts.warnings, 'warning'));
    return parts.join(', ');
}

/** Issues indexed by `subjectId`, server order kept; flow-level issues are under `''`. */
export function issuesBySubject(issues: FlowIssue[]): ReadonlyMap<string, FlowIssue[]> {
    const bySubject = new Map<string, FlowIssue[]>();
    for (const issue of issues) {
        const group = bySubject.get(issue.subjectId);
        if (group) {
            group.push(issue);
        } else {
            bySubject.set(issue.subjectId, [issue]);
        }
    }
    return bySubject;
}

/** How many errors and warnings a list of issues holds. */
export function issueCounts(issues: FlowIssue[]): { errors: number; warnings: number } {
    let errors = 0;
    let warnings = 0;
    for (const issue of issues) {
        if (issue.severity === 'error') {
            errors++;
        } else {
            warnings++;
        }
    }
    return { errors, warnings };
}

/** Tool badge count (18.9): the role's distinct tool ids (an agent carries no tools). */
export function toolCount(role: { toolIds?: string[] } | undefined): number {
    return new Set(role?.toolIds ?? []).size;
}
